import { Injectable, inject, signal } from '@angular/core';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import { clone } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { GlobalAppConfigModel } from '../../../core/models/app-config/global-app-config.model';
import { TodoPlannerConfigModel } from '../../../core/models/app-config/todo-planner-config.model';
import { BaseTodoPlannerModel } from '../../../core/models/todo-planner/base-todo-planner.model';
import { MonthTodoPlannerModel } from '../../../core/models/todo-planner/month-todo-planner.model';
import { TodoEntryModel } from '../../../core/models/todo-planner/todo-entry.model';
import { WeekTodoPlannerModel } from '../../../core/models/todo-planner/week-todo-planner-model';
import { UserModel } from '../../../core/models/user/user.model';
import { TodoPlannerHttpService } from '../../../core/state/todo-planner/todo-planner-http.service';
import { mapToArray } from '../../../shared/functions/map-functions';
import { IFixIntervalViewType } from '../../calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../../calendar/util/date-time-helper';
import { TodoEntryWrapperDirective } from '../directives/todo-entry-wrapper.directive';
import { TodoEntryService } from '../todo-entry/services/todo-entry.service';
import { TodoPlannerViews } from '../utils/todo-planner-types';
import { TodoPlannerParallelIndexCalculatorService } from './todo-planner-parallel-index-calculator.service';

@Injectable({
  providedIn: 'root',
})
export class TodoPlannerService {
  // Signals and subjects
  weekDays = signal<Date[]>([]);
  reloadTodoEntries$ = new Subject<void>();
  sendTodoEntryModel$ = new BehaviorSubject<TodoEntryModel | null>(null);

  public parallelIndexChanged$ = new BehaviorSubject<number>(-1);
  public drawer$ = new BehaviorSubject<DrawerComponent | null>(null);
  public todoPlannerModel: BaseTodoPlannerModel | null = null;
  public activeAddTodoEntryLaneId = signal(-1);

  // DI
  private readonly todoPlannerEntriesHttpService = inject(TodoPlannerHttpService);
  private readonly todoPlannerParallelINdexCalculator = inject(
    TodoPlannerParallelIndexCalculatorService,
  );

  public buildPlannerModel(
    todoPlannerConfigModel: TodoPlannerConfigModel,
    globalAppConfigModel: GlobalAppConfigModel,
    loggedInUser: UserModel,
  ): void {
    const plannerView = todoPlannerConfigModel.defaultPlannerView;
    let plannerModel: WeekTodoPlannerModel | MonthTodoPlannerModel;
    const today = new Date();

    if (plannerView === TodoPlannerViews.WeekView) {
      plannerModel = new WeekTodoPlannerModel();
      plannerModel.plannerStart = DateTimeHelper.startOfWeek(today, {
        weekStartsOn: globalAppConfigModel.weekStartsOn,
      });
      plannerModel.plannerEnd = DateTimeHelper.endOfWeek(today, {
        weekStartsOn: globalAppConfigModel.weekStartsOn,
      });
    } else if (plannerView === TodoPlannerViews.MonthView) {
      plannerModel = new MonthTodoPlannerModel();
      plannerModel.plannerStart = DateTimeHelper.startOfMonth(today);
      plannerModel.plannerEnd = DateTimeHelper.endOfMonth(today);
    }

    plannerModel.plannerViewMode = plannerView;
    plannerModel.weekStartsOn = globalAppConfigModel.weekStartsOn;
    plannerModel.userId = loggedInUser;
    plannerModel.hideTodoEntryDetails = !!todoPlannerConfigModel.hideTodoEntryDetails;
    this.todoPlannerModel = plannerModel;

    this.setPlannerDays();
  }

  public loadTodoEntries(
    plannerStart: Date,
    plannerEnd: Date,
    userId: number,
  ): Observable<TodoEntryModel[]> {
    return this.todoPlannerEntriesHttpService.getTodoPlannerEntries(
      plannerStart,
      plannerEnd,
      userId,
    );
  }

  public getTodoEntryWrapper(
    key: TodoEntryModel | Date,
    isFixInterval = false,
  ): TodoEntryWrapperDirective | undefined {
    const wrapper = isFixInterval
      ? this.todoPlannerModel.fixIntervalTodoWrapper
      : this.todoPlannerModel.variableIntervalTodoWrapper;

    if (key instanceof Date) {
      return wrapper.find((c) => DateTimeHelper.isSameDay(c.date(), key));
    }

    return wrapper.find((c) => DateTimeHelper.isSameDay(c.date(), key.start));
  }

  public setPlannerDays(): void {
    const plannerModel = this.todoPlannerModel;
    DateTimeHelper.eachDayOfInterval(plannerModel.plannerStart, plannerModel.plannerEnd).forEach(
      (day: Date) => {
        plannerModel.fixIntervalTodoEntryModels.set(DateTimeHelper.format(day), []);
        plannerModel.variableIntervalTodoEntryModels.set(DateTimeHelper.format(day), []);
      },
    );

    this.weekDays.set(
      DateTimeHelper.eachDayOfInterval(plannerModel.plannerStart, plannerModel.plannerEnd),
    );
  }

  public calculateMaxParallelIndex(): void {
    const fixIntervalTodoEntryMap = this.cloneTodoPlannerMap(
      this.todoPlannerModel.fixIntervalTodoEntryModels,
    );

    this.todoPlannerParallelINdexCalculator.calculate(fixIntervalTodoEntryMap);
    this.todoPlannerModel.fixIntervalTodoEntryModels =
      this.cloneTodoPlannerMap(fixIntervalTodoEntryMap);

    const fixIntervalTodoEntries = mapToArray(fixIntervalTodoEntryMap, true) as TodoEntryModel[];
    if (fixIntervalTodoEntries.length === 0) {
      this.parallelIndexChanged$.next(-1);
      return;
    }

    const highest = fixIntervalTodoEntries.reduce((prev, cur) => {
      const curParallelIndex = (cur.timeBlockViewType as IFixIntervalViewType).parallelIndex;
      const prevParallelIndex = (prev.timeBlockViewType as IFixIntervalViewType).parallelIndex;

      return prevParallelIndex > curParallelIndex ? prev : cur;
    });

    const highestParallelIndex = (highest.timeBlockViewType as IFixIntervalViewType).parallelIndex;
    this.parallelIndexChanged$.next(highestParallelIndex < 0 ? 0 : highestParallelIndex);
  }

  public getTodoEntryModels(todoEntryModel: TodoEntryModel): Map<string, TodoEntryModel[]> {
    return todoEntryModel.fixInterval
      ? this.todoPlannerModel.fixIntervalTodoEntryModels
      : this.todoPlannerModel.variableIntervalTodoEntryModels;
  }

  public clearAllTodoEntryComponents(): void {
    const m = this.todoPlannerModel;

    for (const c of m.fixIntervalTodoContainer ?? []) {
      c.vcr.clear();
    }
    for (const c of m.variableIntervalTodoContainer ?? []) {
      c.vcr.clear();
    }

    m.fixIntervalTodoEntryModels?.clear?.();
    m.variableIntervalTodoEntryModels?.clear?.();
  }

  private cloneTodoPlannerMap(map: Map<string, TodoEntryModel[]>): Map<string, TodoEntryModel[]> {
    return new Map(
      Array.from(map, ([key, todoEntries]) => [
        key,
        todoEntries.map((todoEntry) => {
          // clone to-do entry
          const clonedTodoEntry = TodoEntryService.cloneTodoModel(todoEntry, false);
          clonedTodoEntry.timeBlockViewType = clone(clonedTodoEntry.timeBlockViewType);
          clonedTodoEntry.componentRef?.setInput('todoEntryModel', clonedTodoEntry);
          return clonedTodoEntry;
        }),
      ]),
    );
  }
}
